import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  Mail, 
  Calendar as CalendarIcon, 
  TrendingUp, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Eye,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Target,
  Building,
  DollarSign,
  Phone,
  MessageSquare,
  ExternalLink,
  Menu,
  X,
  ChevronDown,
  Activity,
  BarChart3,
  MoreVertical,
  ArrowRight,
  GripVertical
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { collection, query, where, getDocs, getDoc, addDoc, setDoc, doc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { scrapeAndExtractInvestorInfo, extractInvestorInfoFromText } from '../services/backend';
// Import ReactCalendar at the top
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
// No FullCalendar CSS imports! All styling is via Tailwind.
// Optionally, add this to your global CSS if you want to fully reset FullCalendar styles:
// .fc { all: unset; }
import { useNavigate, useLocation } from 'react-router-dom';
import { CalendarPage } from './CalendarPage';

// Add custom scrollbar-hide style if not present in Tailwind
// (You can move this to your global CSS if preferred)
const style = `
  .custom-scrollbar-hide::-webkit-scrollbar { display: none; }
  .custom-scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
`;

// Helper to render event content with custom style
export const renderEventContent = (eventInfo) => (
  <div className="flex items-center gap-1">
    <div className={`w-2 h-2 rounded-full ${eventInfo.event.extendedProps.isDemo ? 'bg-blue-500' : 'bg-green-500'}`}></div>
    <span className="truncate font-medium">{eventInfo.event.title}</span>
  </div>
);

// Mock data for demo company
const demoCompanyData = {
  id: 'demo',
  name: 'Stripe',
  tagline: 'The new standard in online payments',
  logo: 'S',
  website: 'https://stripe.com',
  founded: '2010-01-01',
  location: 'San Francisco, CA',
  stage: 'growth',
  isDemo: true
};

const demoInvestor = {
  id: 'demo',
  firm: 'Sequoia Capital',
  contact: 'Aileen Lee',
  title: 'Partner',
  specialization: 'AI, SaaS, B2B',
  stageFocus: 'Series A',
  checkSize: '$1M-$10M',
  portfolio: 'Stripe, DoorDash, Zoom, Notion',
  connection: 'Intro via Patrick Collison',
  email: 'aileen@sequoiacap.com',
  linkedin: 'linkedin.com/in/aileenlee',
  notes: 'Interested in vertical SaaS, strong network in fintech.',
  tier: 'A',
  isDemo: true,
  companyId: 'demo'
};

const demoInvestorsList = [
  demoInvestor,
  { id: 'demo-2', firm: 'Andreessen Horowitz', contact: 'Marc Andreessen', title: 'General Partner', specialization: 'Software, Crypto', stageFocus: 'Seed to Series B', checkSize: '$500K-$5M', portfolio: 'Facebook, Twitter, Airbnb', connection: 'Warm intro from accelerator', email: 'marc@a16z.com', linkedin: 'linkedin.com/in/pmarca', notes: 'Focus on infrastructure and platforms.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-3', firm: 'Accel', contact: 'Luciana Lixandru', title: 'Partner', specialization: 'Enterprise, SaaS', stageFocus: 'Series A', checkSize: '$2M-$15M', portfolio: 'Slack, Dropbox, UiPath', connection: 'YC network', email: 'luciana@accel.com', linkedin: 'linkedin.com/in/lucianalixandru', notes: 'European and US deals.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-4', firm: 'Greylock Partners', contact: 'Reid Hoffman', title: 'Partner', specialization: 'Consumer, Networks', stageFocus: 'Series A-B', checkSize: '$5M-$20M', portfolio: 'LinkedIn, Facebook, Airbnb', connection: 'Advisor intro', email: 'reid@greylock.com', linkedin: 'linkedin.com/in/reidhoffman', notes: 'Strong on network effects businesses.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-5', firm: 'Lightspeed Venture Partners', contact: 'Nicole Quinn', title: 'Partner', specialization: 'Consumer, Marketplaces', stageFocus: 'Seed, Series A', checkSize: '$1M-$10M', portfolio: 'Carta, Affirm, Epic Games', connection: 'Founder intro', email: 'nicole@lsvp.com', linkedin: 'linkedin.com/in/nicolequinn', notes: 'Consumer and fintech focus.', tier: 'B', isDemo: true, companyId: 'demo' },
  { id: 'demo-6', firm: 'Index Ventures', contact: 'Danny Rimer', title: 'Partner', specialization: 'SaaS, Marketplaces', stageFocus: 'Series A', checkSize: '$3M-$15M', portfolio: 'Notion, Figma, Discord', connection: 'Mutual contact', email: 'danny@indexventures.com', linkedin: 'linkedin.com/in/dannyrimer', notes: 'Product-led growth expertise.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-7', firm: 'Bessemer Venture Partners', contact: 'Byron Deeter', title: 'Partner', specialization: 'Cloud, DevTools', stageFocus: 'Series A-B', checkSize: '$5M-$25M', portfolio: 'Twilio, Shopify, Pinterest', connection: 'Conference', email: 'byron@bvp.com', linkedin: 'linkedin.com/in/byrondeeter', notes: 'Cloud and infrastructure specialist.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-8', firm: 'First Round Capital', contact: 'Josh Kopelman', title: 'Partner', specialization: 'Seed, Consumer', stageFocus: 'Seed', checkSize: '$500K-$2M', portfolio: 'Uber, Notion, Flatiron Health', connection: 'Referral', email: 'josh@firstround.com', linkedin: 'linkedin.com/in/joshkopelman', notes: 'Very early stage, product-minded.', tier: 'B', isDemo: true, companyId: 'demo' },
  { id: 'demo-9', firm: 'General Catalyst', contact: 'Niko Bonatsos', title: 'Managing Director', specialization: 'Consumer, Social', stageFocus: 'Seed to Series A', checkSize: '$1M-$10M', portfolio: 'Snap, Stripe, Instacart', connection: 'Warm intro', email: 'niko@gc.com', linkedin: 'linkedin.com/in/nikobonatsos', notes: 'B2C and community-driven products.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-10', firm: 'NEA', contact: 'Scott Sandell', title: 'Chief Executive', specialization: 'Healthcare, Tech', stageFocus: 'Series A-C', checkSize: '$10M-$50M', portfolio: 'Salesforce, Workday, Tableau', connection: 'Board intro', email: 'ssandell@nea.com', linkedin: 'linkedin.com/in/scottsandell', notes: 'Growth stage and enterprise.', tier: 'A', isDemo: true, companyId: 'demo' },
  { id: 'demo-11', firm: 'YC Continuity', contact: 'Anu Hariharan', title: 'Partner', specialization: 'Growth, B2B', stageFocus: 'Series A-B', checkSize: '$5M-$20M', portfolio: 'Stripe, DoorDash, Instacart', connection: 'YC batch', email: 'anu@ycombinator.com', linkedin: 'linkedin.com/in/anuhariharan', notes: 'Post-YC growth rounds.', tier: 'A', isDemo: true, companyId: 'demo' }
];

const demoOutreach = {
  id: 'demo',
  contact: 'Aileen Lee',
  firm: 'Sequoia Capital',
  outreachDate: '2024-07-01',
  method: 'Email',
  status: 'Meeting Booked',
  response: 'Very interested, wants to see growth metrics and technical architecture',
  followUpDate: '2024-07-15',
  materialsSent: 'Pitch Deck, One-pager, Product Demo',
  notes: 'Demo outreach record',
  isDemo: true,
  companyId: 'demo'
};

const demoOutreachesList = [
  demoOutreach,
  { id: 'demo-2', contact: 'Marc Andreessen', firm: 'Andreessen Horowitz', outreachDate: '2024-06-15', method: 'LinkedIn', status: 'Responded', response: 'Asked for metrics deck', followUpDate: '2024-07-01', materialsSent: 'Pitch Deck', notes: 'Follow up sent', isDemo: true, companyId: 'demo' },
  { id: 'demo-3', contact: 'Luciana Lixandru', firm: 'Accel', outreachDate: '2024-06-20', method: 'Email', status: 'New', response: '', followUpDate: '', materialsSent: '', notes: 'Cold outreach', isDemo: true, companyId: 'demo' },
  { id: 'demo-4', contact: 'Reid Hoffman', firm: 'Greylock Partners', outreachDate: '2024-06-25', method: 'Intro', status: 'Meeting Booked', response: 'Wants to discuss go-to-market', followUpDate: '2024-07-10', materialsSent: 'Deck, One-pager', notes: 'Advisor intro', isDemo: true, companyId: 'demo' },
  { id: 'demo-5', contact: 'Nicole Quinn', firm: 'Lightspeed Venture Partners', outreachDate: '2024-07-01', method: 'Email', status: 'No Response', response: '', followUpDate: '2024-07-20', materialsSent: 'Pitch Deck', notes: 'Second touch planned', isDemo: true, companyId: 'demo' },
  { id: 'demo-6', contact: 'Danny Rimer', firm: 'Index Ventures', outreachDate: '2024-06-18', method: 'Email', status: 'Responded', response: 'Requested technical deep-dive', followUpDate: '2024-07-05', materialsSent: 'Deck, Technical Doc', notes: 'Strong interest', isDemo: true, companyId: 'demo' },
  { id: 'demo-7', contact: 'Byron Deeter', firm: 'Bessemer Venture Partners', outreachDate: '2024-06-22', method: 'Conference', status: 'Meeting Booked', response: 'Follow-up from SaaStr', followUpDate: '2024-07-12', materialsSent: 'All materials', notes: 'Conference follow-up', isDemo: true, companyId: 'demo' },
  { id: 'demo-8', contact: 'Josh Kopelman', firm: 'First Round Capital', outreachDate: '2024-06-28', method: 'Referral', status: 'New', response: '', followUpDate: '', materialsSent: '', notes: 'Warm referral', isDemo: true, companyId: 'demo' },
  { id: 'demo-9', contact: 'Niko Bonatsos', firm: 'General Catalyst', outreachDate: '2024-07-02', method: 'Email', status: 'No Response', response: '', followUpDate: '2024-07-22', materialsSent: 'Pitch Deck', notes: 'First touch', isDemo: true, companyId: 'demo' },
  { id: 'demo-10', contact: 'Scott Sandell', firm: 'NEA', outreachDate: '2024-06-10', method: 'Intro', status: 'Meeting Booked', response: 'Board member intro', followUpDate: '2024-07-18', materialsSent: 'Full packet', notes: 'Board intro', isDemo: true, companyId: 'demo' },
  { id: 'demo-11', contact: 'Anu Hariharan', firm: 'YC Continuity', outreachDate: '2024-06-05', method: 'YC Network', status: 'Responded', response: 'Post-demo feedback requested', followUpDate: '2024-07-08', materialsSent: 'Pitch Deck, Demo', notes: 'YC pipeline', isDemo: true, companyId: 'demo' }
];

const demoMeeting = {
  id: 'demo',
  contact: 'Aileen Lee',
  firm: 'Sequoia Capital',
  meetingDate: new Date().toISOString().slice(0, 10),
  meetingTime: '2:00 PM PST',
  attendees: 'You, Co-founder, Aileen, Associate (Jenny)',
  stage: 'Initial Meeting',
  keyQuestions: 'Market size validation, competitive moat, technical scalability',
  keyFeedback: 'Impressed by technical approach, wants to see customer testimonials',
  nextSteps: 'Send customer case studies and technical deep-dive deck',
  probability: 'High',
  deadline: '2024-07-30',
  estimatedCheck: '$3M-$5M',
  notes: 'Demo meeting record',
  isDemo: true,
  companyId: 'demo'
};

const demoMeetingsList = [
  demoMeeting,
  { id: 'demo-2', contact: 'Marc Andreessen', firm: 'Andreessen Horowitz', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '10:00 AM PST', attendees: 'You, Marc', stage: 'Intro Call', keyQuestions: 'Vision and team', keyFeedback: 'Wants full deck', nextSteps: 'Send deck', probability: 'Medium', deadline: '2024-08-01', estimatedCheck: '$2M-$5M', notes: 'Intro call', isDemo: true, companyId: 'demo' },
  { id: 'demo-3', contact: 'Luciana Lixandru', firm: 'Accel', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '4:00 PM GMT', attendees: 'You, Luciana, Principal', stage: 'First Meeting', keyQuestions: 'GTM and unit economics', keyFeedback: 'Strong on product', nextSteps: 'DD materials', probability: 'High', deadline: '2024-07-25', estimatedCheck: '$5M-$10M', notes: 'Accel meeting', isDemo: true, companyId: 'demo' },
  { id: 'demo-4', contact: 'Reid Hoffman', firm: 'Greylock Partners', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '11:00 AM PST', attendees: 'You, Reid, Partner', stage: 'Partner Meeting', keyQuestions: 'Network effects', keyFeedback: 'Aligned on vision', nextSteps: 'Term sheet discussion', probability: 'High', deadline: '2024-08-05', estimatedCheck: '$8M-$15M', notes: 'Greylock', isDemo: true, companyId: 'demo' },
  { id: 'demo-5', contact: 'Nicole Quinn', firm: 'Lightspeed Venture Partners', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '1:00 PM PST', attendees: 'You, Nicole', stage: 'Intro Call', keyQuestions: 'Consumer metrics', keyFeedback: 'TBD', nextSteps: 'Schedule follow-up', probability: 'Medium', deadline: '2024-07-28', estimatedCheck: '$3M-$7M', notes: 'Lightspeed', isDemo: true, companyId: 'demo' },
  { id: 'demo-6', contact: 'Danny Rimer', firm: 'Index Ventures', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '9:00 AM PST', attendees: 'Full team', stage: 'Deep Dive', keyQuestions: 'Technical architecture', keyFeedback: 'Impressed', nextSteps: 'Reference calls', probability: 'High', deadline: '2024-08-10', estimatedCheck: '$5M-$12M', notes: 'Index', isDemo: true, companyId: 'demo' },
  { id: 'demo-7', contact: 'Byron Deeter', firm: 'Bessemer Venture Partners', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '3:00 PM PST', attendees: 'You, Byron, VP', stage: 'Initial Meeting', keyQuestions: 'Cloud strategy', keyFeedback: 'Strong fit', nextSteps: 'Send cloud roadmap', probability: 'High', deadline: '2024-08-02', estimatedCheck: '$10M-$20M', notes: 'Bessemer', isDemo: true, companyId: 'demo' },
  { id: 'demo-8', contact: 'Josh Kopelman', firm: 'First Round Capital', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '2:30 PM EST', attendees: 'You, Josh', stage: 'Seed Discussion', keyQuestions: 'Traction and team', keyFeedback: 'Excited', nextSteps: 'Due diligence', probability: 'Medium', deadline: '2024-07-20', estimatedCheck: '$1M-$2M', notes: 'First Round', isDemo: true, companyId: 'demo' },
  { id: 'demo-9', contact: 'Niko Bonatsos', firm: 'General Catalyst', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '12:00 PM PST', attendees: 'You, Niko, Associate', stage: 'First Meeting', keyQuestions: 'Community growth', keyFeedback: 'Wants more data', nextSteps: 'Send community metrics', probability: 'Medium', deadline: '2024-07-30', estimatedCheck: '$4M-$8M', notes: 'GC', isDemo: true, companyId: 'demo' },
  { id: 'demo-10', contact: 'Scott Sandell', firm: 'NEA', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '10:30 AM PST', attendees: 'You, Scott, Partner', stage: 'Growth Discussion', keyQuestions: 'Scale and efficiency', keyFeedback: 'Enterprise focus', nextSteps: 'Board meeting', probability: 'High', deadline: '2024-08-15', estimatedCheck: '$15M-$30M', notes: 'NEA growth', isDemo: true, companyId: 'demo' },
  { id: 'demo-11', contact: 'Anu Hariharan', firm: 'YC Continuity', meetingDate: new Date().toISOString().slice(0, 10), meetingTime: '4:30 PM PST', attendees: 'You, Anu', stage: 'Follow-up', keyQuestions: 'Post-YC progress', keyFeedback: 'On track', nextSteps: 'Final terms', probability: 'High', deadline: '2024-08-01', estimatedCheck: '$5M-$15M', notes: 'YC Continuity', isDemo: true, companyId: 'demo' }
];

const InvestorTracker = () => {
  const { currentUser } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const selectedCompany = selectedWorkspace; // Global workspace = selected company
  const [activeTab, setActiveTab] = useState('master');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [masterList, setMasterList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  const [newInvestor, setNewInvestor] = useState({
    firm: '',
    contact: '',
    title: '',
    specialization: '',
    stageFocus: '',
    checkSize: '',
    portfolio: '',
    connection: '',
    email: '',
    linkedin: '',
    notes: '',
    tier: 'B',
    userId: '',
    companyId: '',
  });

  const [selectedOutreach, setSelectedOutreach] = useState(null);
  const [outreachTracker, setOutreachTracker] = useState([]);
  const [meetingPipeline, setMeetingPipeline] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateMeetings, setSelectedDateMeetings] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    contact: '',
    firm: '',
    meetingDate: '',
    meetingTime: '',
    attendees: '',
    stage: '',
    keyQuestions: '',
    keyFeedback: '',
    nextSteps: '',
    probability: '',
    deadline: '',
    estimatedCheck: '',
    notes: '',
    isDemo: false,
    companyId: '',
  });
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // AI processing state
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiMethod, setAiMethod] = useState(''); // 'website' or 'text'
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [investorText, setInvestorText] = useState('');
  const [aiError, setAiError] = useState('');
  
  // Outreach status dropdown state
  const [showCustomStatus, setShowCustomStatus] = useState(false);
  const [customStatus, setCustomStatus] = useState('');
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Auto-dismiss success modal after 1 second
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);
  
  const navigate = useNavigate();

  // Fetch investors for current workspace (global selection from sidebar)
  useEffect(() => {
    if (!currentUser || !selectedCompany) return;
    const fetchInvestors = async () => {
      setLoading(true);
      try {
        const investorsRef = collection(db, 'investors');
        const q = query(investorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const investorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sorted = [...investorsData].sort((a, b) => {
          const tA = a.createdAt?.toMillis?.() ?? a.createdAt ?? 0;
          const tB = b.createdAt?.toMillis?.() ?? b.createdAt ?? 0;
          return tB - tA;
        });
        setMasterList(selectedCompany.isDemo ? demoInvestorsList : [demoInvestor, ...sorted]);
      } catch (error) {
        console.error('Error fetching investors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvestors();
  }, [currentUser, selectedCompany]);

  // Fetch outreach for selected company
  useEffect(() => {
    if (!currentUser || !selectedCompany) return;
    const fetchOutreach = async () => {
      try {
        const outreachRef = collection(db, 'outreachTracker');
        const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const outreachData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOutreachTracker(selectedCompany.isDemo ? demoOutreachesList : (outreachData.length === 0 ? [demoOutreach] : outreachData));
      } catch (error) {
        console.error('Error fetching outreach:', error);
      }
    };
    fetchOutreach();
  }, [currentUser, selectedCompany]);

  // Fetch meetings for selected company
  useEffect(() => {
    if (!currentUser || !selectedCompany) return;
    const fetchMeetings = async () => {
      try {
        const meetingsRef = collection(db, 'meetingPipeline');
        const q = query(meetingsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const meetingsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMeetingPipeline(selectedCompany.isDemo ? demoMeetingsList : [demoMeeting, ...meetingsData]);
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    };
    fetchMeetings();
  }, [currentUser, selectedCompany]);

  const handleAddInvestor = async () => {
    if (!selectedCompany) {
      alert('Please select a company first');
      return;
    }
    if (selectedCompany.isDemo) {
      alert('Cannot add investors to demo company. Please select a real company first.');
      return;
    }
    setCreating(true);
    try {
      const investorData = { ...newInvestor, userId: currentUser.uid, companyId: selectedCompany.id, createdAt: serverTimestamp() };
      const docRef = await addDoc(collection(db, 'investors'), investorData);
      setShowModal(false);
      setNewInvestor({
        firm: '', contact: '', title: '', specialization: '', stageFocus: '', checkSize: '', portfolio: '', connection: '', email: '', linkedin: '', notes: '', tier: 'B', userId: '', companyId: ''
      });
      
      // Show success modal
      setSuccessMessage('Investor added successfully!');
      setShowSuccessModal(true);
      // Refresh list
      const investorsRef = collection(db, 'investors');
      const q = query(investorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const investorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = [...investorsData].sort((a, b) => {
        const tA = a.createdAt?.toMillis?.() ?? a.createdAt ?? 0;
        const tB = b.createdAt?.toMillis?.() ?? b.createdAt ?? 0;
        return tB - tA;
      });
      setMasterList([demoInvestor, ...sorted]);

      // --- Outreach Tracker Sync ---
      // Check if outreach already exists for this investor
      const outreachRef = collection(db, 'outreachTracker');
      const outreachQ = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id), where('contact', '==', investorData.contact), where('firm', '==', investorData.firm));
      const outreachSnap = await getDocs(outreachQ);
      if (outreachSnap.empty) {
        // Create outreach card
        await addDoc(collection(db, 'outreachTracker'), {
          contact: investorData.contact,
          firm: investorData.firm,
          outreachDate: new Date().toISOString().slice(0, 10),
          method: 'Not Set',
          status: 'New',
          userId: currentUser.uid,
          companyId: selectedCompany.id,
          isDemo: false,
          investorId: docRef.id
        });
        // Refresh outreach tracker
        const q2 = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const snap2 = await getDocs(q2);
        const outreachData = snap2.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
      }
    } catch (error) {
      console.error('Error adding investor:', error);
    } finally {
      setCreating(false);
    }
  };

  const updateInvestor = async (investor) => {
    try {
      const docRef = collection(db, 'investors');
      // Only update if not demo
      if (!investor.isDemo && investor.id) {
        const { id, ...data } = investor;
        await setDoc(doc(db, 'investors', id), data);
        // Refresh list
        const investorsRef = collection(db, 'investors');
        const q = query(investorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const investorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sorted = [...investorsData].sort((a, b) => {
          const tA = a.createdAt?.toMillis?.() ?? a.createdAt ?? 0;
          const tB = b.createdAt?.toMillis?.() ?? b.createdAt ?? 0;
          return tB - tA;
        });
        setMasterList([demoInvestor, ...sorted]);
      }
    } catch (error) {
      console.error('Error updating investor:', error);
    }
  };

  const updateOutreach = async (outreach) => {
    try {
      const docRef = collection(db, 'outreachTracker');
      if (!outreach.isDemo && outreach.id) {
        const { id, ...data } = outreach;
        await setDoc(doc(db, 'outreachTracker', id), data);
        // Refresh list
        const outreachRef = collection(db, 'outreachTracker');
        const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const outreachData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
        
        // Show success message
        setSuccessMessage('Outreach updated successfully!');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error updating outreach:', error);
    }
  };

  const deleteOutreach = async (id) => {
    try {
      await deleteDoc(doc(db, 'outreachTracker', id));
      // Refresh list
      const outreachRef = collection(db, 'outreachTracker');
      const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const outreachData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
      
      // Show success message
      setSuccessMessage('Outreach deleted successfully!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error deleting outreach:', error);
    }
  };

  const updateMeeting = async (pipeline) => {
    try {
      const docRef = collection(db, 'meetingPipeline');
      if (!pipeline.isDemo && pipeline.id) {
        const { id, ...data } = pipeline;
        await setDoc(doc(db, 'meetingPipeline', id), data);
        // Refresh list
        const pipelineRef = collection(db, 'meetingPipeline');
        const q = query(pipelineRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const pipelineData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMeetingPipeline([demoMeeting, ...pipelineData]);
      }
    } catch (error) {
      console.error('Error updating meeting:', error);
    }
  };

  const deleteMeeting = async (id) => {
    try {
      await deleteDoc(doc(db, 'meetingPipeline', id));
      // Refresh list
      const pipelineRef = collection(db, 'meetingPipeline');
      const q = query(pipelineRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const pipelineData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMeetingPipeline([demoMeeting, ...pipelineData]);
    } catch (error) {
      console.error('Error deleting meeting:', error);
    }
  };

  const deleteInvestor = async (id) => {
    console.log('🔍 Starting deleteInvestor function');
    console.log('📋 Delete parameters:', { id, currentUser: currentUser?.uid, selectedCompany: selectedCompany?.id });
    
    // Check if trying to delete demo investor
    if (id === 'demo' || (typeof id === 'string' && id.startsWith('demo-'))) {
      console.log('⚠️ Attempting to delete demo investor - blocking');
      alert('Cannot delete demo investor');
      return;
    }
    
    try {
      console.log('🗑️ Attempting to delete document from investors collection');
      
      // Check if document exists before deleting
      const docRef = doc(db, 'investors', id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.log('❌ Document does not exist:', id);
        alert('Investor not found. It may have already been deleted.');
        return;
      }
      
      console.log('📄 Document exists, proceeding with delete');
      await deleteDoc(docRef);
      console.log('✅ Document deleted successfully');
      
      // Refresh list
      console.log('🔄 Refreshing investor list');
      const investorsRef = collection(db, 'investors');
      const q = query(investorsRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
      const querySnapshot = await getDocs(q);
      const investorsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = [...investorsData].sort((a, b) => {
        const tA = a.createdAt?.toMillis?.() ?? a.createdAt ?? 0;
        const tB = b.createdAt?.toMillis?.() ?? b.createdAt ?? 0;
        return tB - tA;
      });
      console.log('📊 Found investors after delete:', sorted.length);
      setMasterList([demoInvestor, ...sorted]);
      
      // Show success message
      setSuccessMessage('Investor deleted successfully!');
      setShowSuccessModal(true);
      console.log('🎉 Delete operation completed successfully');
    } catch (error) {
      console.error('❌ Error deleting investor:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    }
  };

  const updateOutreachStatus = async (outreachId, newStatus) => {
    try {
      // Always fetch the latest outreach doc from Firestore
      const outreachDocRef = doc(db, 'outreachTracker', outreachId);
      const outreachSnap = await getDocs(query(collection(db, 'outreachTracker'), where('__name__', '==', outreachId)));
      let outreach;
      if (!outreachSnap.empty) {
        outreach = { id: outreachId, ...outreachSnap.docs[0].data() };
      } else {
        outreach = demoOutreach;
      }
      if (!outreach.isDemo && outreach.id) {
        const updatedOutreach = { ...outreach, status: newStatus };
        await setDoc(outreachDocRef, updatedOutreach);
        // Refresh list
        const outreachRef = collection(db, 'outreachTracker');
        const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
        const querySnapshot = await getDocs(q);
        const outreachData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
      }
      setShowStatusMenu(null);
    } catch (error) {
      console.error('Error updating outreach status:', error);
    }
  };

  // AI processing functions
  const handleWebsiteScraping = async () => {
    if (!websiteUrl.trim()) {
      setAiError('Please enter a website URL');
      return;
    }
    
    setAiProcessing(true);
    setAiError('');
    
    try {
      const result = await scrapeAndExtractInvestorInfo(websiteUrl);
      if (result.success) {
        setNewInvestor(prev => ({
          ...prev,
          ...result.data,
          tier: prev.tier // Keep the existing tier
        }));
        setWebsiteUrl('');
        setAiMethod('');
      } else {
        // Check if it's a scraping failure and suggest text method
        if (result.error && (result.error.includes('CORS') || result.error.includes('unavailable') || result.error.includes('network'))) {
          setAiError(`${result.error} 💡 Try using the "Text Analysis" method instead - you can paste investor information directly.`);
        } else {
          setAiError(result.error || 'Failed to extract investor information');
        }
      }
    } catch (error) {
      setAiError('An error occurred while processing the website. 💡 Try using the "Text Analysis" method instead.');
      console.error('Error processing website:', error);
    } finally {
      setAiProcessing(false);
    }
  };

  const handleTextExtraction = async () => {
    if (!investorText.trim()) {
      setAiError('Please enter investor information');
      return;
    }
    
    setAiProcessing(true);
    setAiError('');
    
    try {
      const result = await extractInvestorInfoFromText(investorText);
      if (result.success) {
        setNewInvestor(prev => ({
          ...prev,
          ...result.data,
          tier: prev.tier // Keep the existing tier
        }));
        setInvestorText('');
        setAiMethod('');
      } else {
        setAiError(result.error || 'Failed to extract investor information');
      }
    } catch (error) {
      setAiError('An error occurred while processing the text');
      console.error('Error processing text:', error);
    } finally {
      setAiProcessing(false);
    }
  };

  // Close status menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusMenu && !event.target.closest('.status-menu')) {
        setShowStatusMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusMenu]);

  // Monitor delete modal state
  useEffect(() => {
    console.log('🔍 Delete modal state changed:', showDeleteModal);
  }, [showDeleteModal]);

  // Drag and drop functions
  const handleDragStart = (e, outreach) => {
    setDraggedItem(outreach);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, columnStatus) => {
    e.preventDefault();
    setDragOverColumn(columnStatus);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    if (draggedItem && draggedItem.status !== targetStatus) {
      await updateOutreachStatus(draggedItem.id, targetStatus);
    }
    setDraggedItem(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Contacted': return 'bg-yellow-100 text-yellow-800';
      case 'Meeting Scheduled': return 'bg-green-100 text-green-800';
      case 'Replied': return 'bg-purple-100 text-purple-800';
      case 'Meeting Booked': return 'bg-green-100 text-green-800';
      case 'Sent': return 'bg-gray-100 text-gray-800';
      case 'Opened': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'A': return 'bg-red-100 text-red-800';
      case 'B': return 'bg-orange-100 text-orange-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProbabilityColor = (probability) => {
    switch (probability) {
      case 'High': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Initial Meeting': return 'bg-blue-100 text-blue-800';
      case 'Partner Meeting': return 'bg-purple-100 text-purple-800';
      case 'Due Diligence': return 'bg-orange-100 text-orange-800';
      case 'Technical Deep-Dive': return 'bg-indigo-100 text-indigo-800';
      case 'Term Sheet': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredMasterList = masterList.filter(investor => {
    const matchesSearch = investor.firm.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investor.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investor.specialization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || investor.tier === filterTier;
    const matchesStage = filterStage === 'all' || investor.stageFocus === filterStage;
    return matchesSearch && matchesTier && matchesStage;
  });

  const statsData = {
    totalInvestors: masterList.length,
    contacted: outreachTracker.length,
    meetings: meetingPipeline.length,
    responseRate: Math.round((outreachTracker.filter(o => o.status === 'Replied' || o.status === 'Meeting Booked').length / outreachTracker.length) * 100) || 0,
    tierAInvestors: masterList.filter(i => i.tier === 'A').length,
    highProbability: meetingPipeline.filter(m => m.probability === 'High').length
  };

  const outreachColumns = [
    {
      key: 'todo',
      title: 'To Do',
      statuses: ['New', 'To Do'],
      color: 'bg-gray-100 text-gray-600',
      border: 'border-blue-200',
      bg: 'from-blue-50 to-indigo-50',
      ring: 'ring-blue-400 bg-blue-50/50',
    },
    {
      key: 'inprogress',
      title: 'In Progress',
      statuses: ['Sent', 'Opened', 'Contacted'],
      color: 'bg-yellow-100 text-yellow-600',
      border: 'border-yellow-200',
      bg: 'from-yellow-50 to-orange-50',
      ring: 'ring-yellow-400 bg-yellow-50/50',
    },
    {
      key: 'meeting',
      title: 'Meeting Booked',
      statuses: ['Meeting Booked'],
      color: 'bg-green-100 text-green-600',
      border: 'border-green-200',
      bg: 'from-green-50 to-emerald-50',
      ring: 'ring-green-400 bg-green-50/50',
    },
    {
      key: 'completed',
      title: 'Completed',
      statuses: ['Replied', 'Completed'],
      color: 'bg-purple-100 text-purple-600',
      border: 'border-purple-200',
      bg: 'from-purple-50 to-indigo-50',
      ring: 'ring-purple-400 bg-purple-50/50',
    },
  ];

  const kanbanScrollRef = useRef(null);
  const kanbanColumnRefs = useRef([]);
  const [activeKanbanCol, setActiveKanbanCol] = useState(0);

  const handleKanbanScroll = () => {
    if (!kanbanScrollRef.current) return;
    const scrollLeft = kanbanScrollRef.current.scrollLeft;
    const colWidths = kanbanColumnRefs.current.map(ref => ref?.offsetLeft || 0);
    let activeIdx = 0;
    for (let i = 0; i < colWidths.length; i++) {
      if (scrollLeft + 40 >= colWidths[i]) activeIdx = i;
    }
    setActiveKanbanCol(activeIdx);
  };

  const scrollToKanbanCol = idx => {
    if (!kanbanScrollRef.current || !kanbanColumnRefs.current[idx]) return;
    kanbanScrollRef.current.scrollTo({
      left: kanbanColumnRefs.current[idx].offsetLeft,
      behavior: 'smooth',
    });
    setActiveKanbanCol(idx);
  };

  useEffect(() => {
    const openModal = () => setShowModal(true);
    window.addEventListener('openAddInvestorModal', openModal);
    return () => window.removeEventListener('openAddInvestorModal', openModal);
  }, []);

  return (
    <>
      <style>{style}</style>
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 py-4 sm:py-8 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Action Buttons - Add Investor & Calendar hidden on mobile */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto mb-6">
            <button
              onClick={() => setShowModal(true)}
              disabled={!selectedCompany || selectedCompany.isDemo}
              className="hidden sm:inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={20} className="mr-2" />
              Add Investor
              {selectedCompany?.isDemo && (
                <span className="ml-2 text-xs opacity-75">(Demo - Add real company first)</span>
              )}
            </button>
            <button
              onClick={() => navigate('/investor-tracker/calendar', { state: { meetingPipeline, demoMeeting } })}
              className="hidden sm:inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 w-full sm:w-auto"
            >
              <CalendarIcon size={20} className="mr-2" />
              Calendar
            </button>
            {/* Stats button - mobile only */}
            <button
              onClick={() => setShowStatsModal(true)}
              className="sm:hidden inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 transition-all duration-200 w-full"
            >
              <BarChart3 size={20} className="mr-2" />
              Stats
            </button>
          </div>
          {/* Analytics Cards - desktop only; on mobile use Stats modal */}
          <div className="hidden sm:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">Total Investors</span>
              <span className="text-2xl font-bold text-blue-700">{statsData.totalInvestors}</span>
            </div>
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">Tier A Investors</span>
              <span className="text-2xl font-bold text-red-600">{statsData.tierAInvestors}</span>
            </div>
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">Outreach Sent</span>
              <span className="text-2xl font-bold text-purple-700">{outreachTracker.length}</span>
            </div>
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">Response Rate</span>
              <span className="text-2xl font-bold text-green-600">{statsData.responseRate}%</span>
            </div>
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">Meetings Booked</span>
              <span className="text-2xl font-bold text-indigo-700">{meetingPipeline.length}</span>
            </div>
            <div className="bg-white/90 rounded-2xl shadow flex flex-col items-center p-4">
              <span className="text-xs text-gray-500 font-medium mb-1">High Probability Deals</span>
              <span className="text-2xl font-bold text-emerald-600">{statsData.highProbability}</span>
            </div>
          </div>

          {/* Stats Modal - mobile */}
          {showStatsModal && (
            <div className="sm:hidden fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowStatsModal(false)}>
              <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Investor Stats</h3>
                  <button onClick={() => setShowStatsModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Close">
                    <X size={20} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">Total Investors</span>
                    <span className="text-2xl font-bold text-blue-700">{statsData.totalInvestors}</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">Tier A Investors</span>
                    <span className="text-2xl font-bold text-red-600">{statsData.tierAInvestors}</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">Outreach Sent</span>
                    <span className="text-2xl font-bold text-purple-700">{outreachTracker.length}</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">Response Rate</span>
                    <span className="text-2xl font-bold text-green-600">{statsData.responseRate}%</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">Meetings Booked</span>
                    <span className="text-2xl font-bold text-indigo-700">{meetingPipeline.length}</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl flex flex-col items-center p-4">
                    <span className="text-xs text-gray-500 font-medium mb-1">High Probability Deals</span>
                    <span className="text-2xl font-bold text-emerald-600">{statsData.highProbability}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="overflow-x-auto px-0 mb-1 sm:mb-2">
            <div className="flex flex-nowrap gap-x-2 overflow-x-auto bg-white/80 rounded-xl shadow-lg p-1">
              <button
                onClick={() => setActiveTab('master')}
                className={`flex-1 min-w-[120px] flex flex-col items-center py-2 px-3 transition-all rounded-full
                  ${activeTab === 'master'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold shadow-lg text-base'
                    : 'text-gray-500 hover:text-indigo-600 bg-transparent'}
                `}
              >
                <Users size={22} className="mb-0.5 sm:mb-1" />
                <span className="text-xs sm:text-sm leading-tight">Master Investor List</span>
              </button>
              <button
                onClick={() => setActiveTab('outreach')}
                className={`flex-1 min-w-[120px] flex flex-col items-center py-2 px-3 transition-all rounded-full
                  ${activeTab === 'outreach'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold shadow-lg text-base'
                    : 'text-gray-500 hover:text-indigo-600 bg-transparent'}
                `}
              >
                <Mail size={22} className="mb-0.5 sm:mb-1" />
                <span className="text-xs sm:text-sm leading-tight">Outreach Tracker</span>
              </button>
            </div>
          </div>
          {/* Tab Content */}
          <div className="w-full">
            {activeTab === 'master' && (
              <>
                {/* Search - just above list/table */}
                <div className="mb-4">
                  <div className="flex items-center bg-white/95 rounded-xl shadow-md px-3 py-2.5 sm:px-4 sm:py-2 w-full min-h-[44px] sm:min-h-0">
                    <Search size={20} className="text-gray-500 mr-2 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Search investors by firm, contact, or focus..."
                      className="flex-1 min-w-0 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400 text-base sm:text-sm"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                {/* Add Investor Modal */}
                {showModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => {
                    setShowModal(false);
                    setAiMethod('');
                    setWebsiteUrl('');
                    setInvestorText('');
                    setAiError('');
                  }}>
                    <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-xl shadow-2xl relative max-h-[90vh] overflow-y-auto flex flex-col justify-between" onClick={e => e.stopPropagation()}>
                      <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => {
                        setShowModal(false);
                        setAiMethod('');
                        setWebsiteUrl('');
                        setInvestorText('');
                        setAiError('');
                      }}><X size={24} /></button>
                      <h2 className="text-xl font-bold mb-4">Add Investor</h2>
                      {selectedCompany?.isDemo && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-yellow-700 text-sm">
                            <strong>Demo Company Selected:</strong> You cannot add investors to the demo company. 
                            Please select a real company from the dropdown above to add investors.
                          </p>
                        </div>
                      )}
                      
                      {/* Quick fill: URL or paste text to populate form */}
                      <div className={`mb-6 p-4 sm:p-5 rounded-xl border ${selectedCompany?.isDemo ? 'bg-gray-50 border-gray-200' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'}`}>
                        <h3 className="text-base sm:text-lg font-semibold mb-1 text-gray-800">Quick fill form</h3>
                        <p className="text-sm text-gray-600 mb-4">Enter a URL or paste investor bio text to auto-populate the fields below.</p>
                        {selectedCompany?.isDemo && (
                          <p className="text-gray-500 text-sm mb-3">AI features disabled for demo company</p>
                        )}
                        
                        {/* Method Selection - stack on mobile */}
                        <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
                          <button
                            onClick={() => setAiMethod('website')}
                            disabled={selectedCompany?.isDemo}
                            className={`flex-1 min-w-0 px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-all ${
                              aiMethod === 'website' 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            } ${selectedCompany?.isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            🌐 Paste URL
                          </button>
                          <button
                            onClick={() => setAiMethod('text')}
                            disabled={selectedCompany?.isDemo}
                            className={`flex-1 min-w-0 px-4 py-3 sm:py-2 rounded-lg text-sm font-medium transition-all ${
                              aiMethod === 'text' 
                                ? 'bg-blue-600 text-white shadow-lg' 
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            } ${selectedCompany?.isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            📝 Paste bio text
                          </button>
                        </div>

                        {/* Website URL Input - stack on mobile */}
                        {aiMethod === 'website' && (
                          <div className="space-y-3">
                            <label className="text-xs font-medium text-gray-600 block">Investor or firm website URL</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input
                                type="url"
                                placeholder="https://sequoiacap.com"
                                value={websiteUrl}
                                onChange={(e) => setWebsiteUrl(e.target.value)}
                                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-3 sm:py-2 text-sm"
                                disabled={aiProcessing}
                              />
                              <button
                                onClick={handleWebsiteScraping}
                                disabled={aiProcessing || !websiteUrl.trim()}
                                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                              >
                                {aiProcessing ? 'Processing…' : 'Extract'}
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">If URL fails, use “Paste bio text” and paste from the site instead.</p>
                          </div>
                        )}

                        {/* Text Input - stack on mobile */}
                        {aiMethod === 'text' && (
                          <div className="space-y-3">
                            <label className="text-xs font-medium text-gray-600 block">Paste investor or firm bio text</label>
                            <textarea
                              placeholder="e.g. Sequoia Capital, VC focused on AI/SaaS. Contact: Aileen Lee, Partner. Invests $1M–$10M Series A. Portfolio: Stripe, DoorDash."
                              value={investorText}
                              onChange={(e) => setInvestorText(e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm min-h-[100px] resize-y"
                              disabled={aiProcessing}
                            />
                            <div className="flex flex-col-reverse sm:flex-row gap-2">
                              <button
                                onClick={() => {
                                  setAiMethod('');
                                  setWebsiteUrl('');
                                  setInvestorText('');
                                  setAiError('');
                                }}
                                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-all"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleTextExtraction}
                                disabled={aiProcessing || !investorText.trim()}
                                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0"
                              >
                                {aiProcessing ? 'Processing…' : 'Extract & fill form'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Error Display */}
                        {aiError && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{aiError}</p>
                            {aiError.includes('Text Analysis') && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-blue-700 text-xs">Try “Paste bio text” above and paste from the website instead.</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Success Message */}
                        {!aiMethod && !aiError && (newInvestor.firm || newInvestor.contact) && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-600 text-sm">✅ AI data loaded! Review and edit the fields below.</p>
                          </div>
                        )}
                      </div>

                      <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 py-4 ${selectedCompany?.isDemo ? 'opacity-50' : ''}`}>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Investor Firm</label>
                          <input 
                            className="border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0" 
                            placeholder="Investor Firm" 
                            value={newInvestor.firm} 
                            onChange={e => setNewInvestor({ ...newInvestor, firm: e.target.value })} 
                            disabled={selectedCompany?.isDemo}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Contact Name</label>
                          <input className="border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0" placeholder="Contact Name" value={newInvestor.contact} onChange={e => setNewInvestor({ ...newInvestor, contact: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Title</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Title" value={newInvestor.title} onChange={e => setNewInvestor({ ...newInvestor, title: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Specialization</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Specialization" value={newInvestor.specialization} onChange={e => setNewInvestor({ ...newInvestor, specialization: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Stage Focus</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Stage Focus" value={newInvestor.stageFocus} onChange={e => setNewInvestor({ ...newInvestor, stageFocus: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Check Size</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Check Size" value={newInvestor.checkSize} onChange={e => setNewInvestor({ ...newInvestor, checkSize: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Portfolio Companies</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Portfolio Companies" value={newInvestor.portfolio} onChange={e => setNewInvestor({ ...newInvestor, portfolio: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Connection</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Connection" value={newInvestor.connection} onChange={e => setNewInvestor({ ...newInvestor, connection: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Email</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Email" value={newInvestor.email} onChange={e => setNewInvestor({ ...newInvestor, email: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">LinkedIn</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="LinkedIn" value={newInvestor.linkedin} onChange={e => setNewInvestor({ ...newInvestor, linkedin: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1 md:col-span-2">
                          <label className="text-xs font-medium text-gray-700">Notes</label>
                          <input className="border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0" placeholder="Notes" value={newInvestor.notes} onChange={e => setNewInvestor({ ...newInvestor, notes: e.target.value })} disabled={selectedCompany?.isDemo} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Tier</label>
                          <select className="border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0" value={newInvestor.tier} onChange={e => setNewInvestor({ ...newInvestor, tier: e.target.value })} disabled={selectedCompany?.isDemo}>
                            <option value="A">Tier A</option>
                            <option value="B">Tier B</option>
                            <option value="C">Tier C</option>
                          </select>
                        </div>
                      </div>
                      <div className="pt-4 pb-2 sticky bottom-0 bg-white z-10 border-t border-gray-100 mt-2">
                        <button 
                          onClick={handleAddInvestor} 
                          disabled={creating || selectedCompany?.isDemo} 
                          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 sm:py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 text-base sm:text-sm min-h-[48px] sm:min-h-0"
                        >
                          {creating ? 'Adding...' : selectedCompany?.isDemo ? 'Demo Company - Add Real Company First' : 'Add Investor'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Mobile: investor cards */}
                <div className="md:hidden space-y-3 py-4">
                  {filteredMasterList.map(investor => (
                    <div
                      key={investor.id}
                      onClick={() => setSelectedInvestor(investor)}
                      className="bg-white/95 rounded-xl shadow-lg p-4 active:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">{investor.firm}</div>
                          <div className="text-xs text-gray-500 truncate">{investor.contact}{investor.title ? ` · ${investor.title}` : ''}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTierColor(investor.tier)}`}>
                            {investor.tier}
                          </span>
                          <span className="text-xs text-gray-500 truncate max-w-[80px]">{investor.status}</span>
                        </div>
                      </div>
                      {(investor.lastContact || investor.nextAction) && (
                        <div className="mt-2 text-xs text-gray-500 truncate">
                          {investor.lastContact && `Last: ${investor.lastContact}`}
                          {investor.lastContact && investor.nextAction && ' · '}
                          {investor.nextAction && `Next: ${investor.nextAction}`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop: table */}
                <div className="hidden md:block overflow-x-auto py-4">
                  <table className="min-w-full bg-white/90 rounded-2xl shadow-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                      <tr>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Firm</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Contact</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Title</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Specialization</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Stage Focus</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Check Size</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Portfolio</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Connection</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">LinkedIn</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Notes</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Tier</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Last Contact</th>
                        <th className="px-4 py-4 text-left text-sm font-semibold">Next Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredMasterList.map(investor => (
                        <tr
                          key={investor.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedInvestor(investor)}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              {investor.firm}
                              {investor.isDemo && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.contact}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.title}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.specialization}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.stageFocus}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.checkSize}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.portfolio}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.connection}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.email}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.linkedin}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.notes}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierColor(investor.tier)}`}>
                              {investor.tier}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.status}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.lastContact}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{investor.nextAction}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {selectedInvestor && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => setSelectedInvestor(null)}>
                    <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-xl shadow-2xl relative max-h-[90vh] overflow-y-auto flex flex-col justify-between" onClick={e => e.stopPropagation()}>
                      <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setSelectedInvestor(null)}><X size={24} /></button>
                      <div className="flex items-center mb-4">
                        <h2 className="text-xl font-bold flex-1">Investor Details</h2>
                        {selectedInvestor.isDemo && (
                          <span className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Investor Firm</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Investor Firm" value={selectedInvestor.firm} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, firm: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Contact Name</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Contact Name" value={selectedInvestor.contact} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, contact: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Title</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Title" value={selectedInvestor.title} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, title: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Specialization</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Specialization" value={selectedInvestor.specialization} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, specialization: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Stage Focus</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Stage Focus" value={selectedInvestor.stageFocus} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, stageFocus: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Check Size</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Check Size" value={selectedInvestor.checkSize} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, checkSize: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Portfolio Companies</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Portfolio Companies" value={selectedInvestor.portfolio} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, portfolio: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Connection</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Connection" value={selectedInvestor.connection} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, connection: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Email</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Email" value={selectedInvestor.email} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, email: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">LinkedIn</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="LinkedIn" value={selectedInvestor.linkedin} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, linkedin: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Notes</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Notes" value={selectedInvestor.notes} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, notes: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Tier</label>
                          <select className="border rounded-lg px-3 py-2" value={selectedInvestor.tier} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, tier: e.target.value })}>
                            <option value="A">Tier A</option>
                            <option value="B">Tier B</option>
                            <option value="C">Tier C</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Status</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Status" value={selectedInvestor.status} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, status: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Last Contact</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Last Contact" value={selectedInvestor.lastContact} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, lastContact: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Next Action</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Next Action" value={selectedInvestor.nextAction} disabled={selectedInvestor.isDemo} onChange={e => setSelectedInvestor({ ...selectedInvestor, nextAction: e.target.value })} />
                        </div>
                      </div>
                      {!selectedInvestor.isDemo && (
                        <div className="flex flex-col gap-2 mt-6">
                          <button onClick={async () => { await updateInvestor(selectedInvestor); setSelectedInvestor(null); }} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all">Save Changes</button>
                          <button 
                            onClick={() => {
                              console.log('🔍 Investor delete button clicked');
                              console.log('📋 Selected investor:', selectedInvestor);
                              if (selectedInvestor.isDemo) {
                                alert('Cannot delete demo investor');
                                return;
                              }
                              console.log('🎯 Setting delete modal state');
                              setShowDeleteModal({ type: 'investor', id: selectedInvestor.id });
                              console.log('✅ Delete modal state set');
                            }} 
                            disabled={selectedInvestor.isDemo}
                            className="w-full bg-red-500 text-white font-bold py-2 rounded-xl shadow-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {selectedInvestor.isDemo ? 'Demo - Cannot Delete' : 'Delete'}
                          </button>
                          {/* Outreach Tracker Add/Remove */}
                          {(() => {
                            const inOutreach = outreachTracker.some(o => o.investorId === selectedInvestor.id);
                            if (inOutreach) {
                              return (
                                <button
                                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-purple-600 hover:to-pink-700 transition-all"
                                  onClick={async () => {
                                    // Remove from outreachTracker
                                    const toRemove = outreachTracker.find(o => o.investorId === selectedInvestor.id);
                                    if (toRemove) {
                                      await deleteDoc(doc(db, 'outreachTracker', toRemove.id));
                                      // Refresh outreachTracker
                                      const outreachRef = collection(db, 'outreachTracker');
                                      const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
                                      const snap = await getDocs(q);
                                      const outreachData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                                      setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
                                    }
                                  }}
                                >Remove from Outreach Tracker</button>
                              );
                            } else {
                              return (
                                <button
                                  className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-green-600 hover:to-blue-700 transition-all"
                                  onClick={async () => {
                                    // Add to outreachTracker
                                    await addDoc(collection(db, 'outreachTracker'), {
                                      contact: selectedInvestor.contact,
                                      firm: selectedInvestor.firm,
                                      outreachDate: new Date().toISOString().slice(0, 10),
                                      method: 'Not Set',
                                      status: 'New',
                                      userId: currentUser.uid,
                                      companyId: selectedCompany.id,
                                      isDemo: false,
                                      investorId: selectedInvestor.id
                                    });
                                    // Refresh outreachTracker
                                    const outreachRef = collection(db, 'outreachTracker');
                                    const q = query(outreachRef, where('userId', '==', currentUser.uid), where('companyId', '==', selectedCompany.id));
                                    const snap = await getDocs(q);
                                    const outreachData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                                    setOutreachTracker(outreachData.length === 0 ? [demoOutreach] : outreachData);
                                  }}
                                >Add to Outreach Tracker</button>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'outreach' && (
              <>
                {/* Kanban Board - Horizontal scrollable on mobile */}
                <div className="py-4">
                  <div
                    className="flex flex-row gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 overflow-x-auto custom-scrollbar-hide snap-x snap-mandatory"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                    ref={kanbanScrollRef}
                    onScroll={handleKanbanScroll}
                  >
                    {outreachColumns.map((col, idx) => (
                      <div
                        key={col.key}
                        ref={el => (kanbanColumnRefs.current[idx] = el)}
                        className={`snap-start min-w-[80vw] sm:min-w-0 sm:w-auto ${col.bg} bg-white/90 rounded-2xl shadow-lg p-3 sm:p-4 min-h-[400px] transition-all duration-200 ${dragOverColumn === col.statuses[0] ? col.ring : ''}`}
                        onDragOver={e => handleDragOver(e, col.statuses[0])}
                        onDragLeave={handleDragLeave}
                        onDrop={e => handleDrop(e, col.statuses[0])}
                      >
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-800">{col.title}</h3>
                          <span className={`${col.color} text-xs font-medium px-2 py-1 rounded-full`}>
                            {(selectedCompany?.isDemo ? outreachTracker : [demoOutreach, ...outreachTracker]).filter(o => col.statuses.includes(o.status)).length}
                          </span>
                        </div>
                        <div className="space-y-2 sm:space-y-3">
                          {(selectedCompany?.isDemo ? outreachTracker : [demoOutreach, ...outreachTracker])
                            .filter(outreach => col.statuses.includes(outreach.status))
                            .map((outreach) => (
                              <div
                                key={outreach.id}
                                draggable={!outreach.isDemo}
                                onDragStart={(e) => !outreach.isDemo && handleDragStart(e, outreach)}
                                onDragEnd={handleDragEnd}
                                className={`bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 sm:p-4 hover:shadow-md transition-all duration-200 relative ${
                                  !outreach.isDemo ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                                } ${draggedItem?.id === outreach.id ? 'opacity-50' : ''}`}
                              >
                                 <div className="flex items-start justify-between mb-2">
                                   <div className="flex items-start gap-2 flex-1">
                                     {!outreach.isDemo && (
                                       <div className="mt-1 cursor-grab active:cursor-grabbing">
                                         <GripVertical size={14} className="text-gray-400" />
                                       </div>
                                     )}
                                     <div className="flex-1 cursor-pointer" onClick={() => setSelectedOutreach(outreach)}>
                                       <h4 className="font-semibold text-gray-900 text-sm">{outreach.contact}</h4>
                                       <p className="text-gray-600 text-xs">{outreach.firm}</p>
                                     </div>
                                   </div>
                                   <div className="flex items-center gap-2">
                                     {outreach.isDemo && (
                                       <span className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
                                     )}
                                     <button
                                       onClick={(e) => {
                                         e.stopPropagation();
                                         setShowStatusMenu(showStatusMenu === outreach.id ? null : outreach.id);
                                       }}
                                       className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                     >
                                       <MoreVertical size={16} className="text-gray-500" />
                                     </button>
                                   </div>
                                 </div>
                                 <div className="flex items-center gap-2 mb-2">
                                   <span className="text-xs text-gray-500">{outreach.method}</span>
                                   <span className="text-xs text-gray-500">•</span>
                                   <span className="text-xs text-gray-500">{outreach.outreachDate}</span>
                                 </div>
                                 {outreach.notes && (
                                   <p className="text-xs text-gray-600 line-clamp-2">{outreach.notes}</p>
                                 )}
                                 
                                 {/* Status Menu */}
                                 {showStatusMenu === outreach.id && (
                                   <div className="absolute top-12 right-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px] status-menu">
                                     <div className="py-1">
                                       <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b">Change Status</div>
                                       {['New', 'Sent', 'Opened', 'Contacted', 'Meeting Booked', 'Replied', 'Completed'].map((status) => (
                                         <button
                                           key={status}
                                           onClick={() => updateOutreachStatus(outreach.id, status)}
                                           className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                                         >
                                           <span>{status}</span>
                                           {outreach.status === status && (
                                             <CheckCircle size={14} className="text-green-500" />
                                           )}
                                         </button>
                                       ))}
                                     </div>
                                   </div>
                                 )}
                               </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Dot Pagination - only on mobile */}
                  <div className="flex justify-center mt-4 sm:hidden">
                    {outreachColumns.map((_, idx) => (
                      <button
                        key={idx}
                        className={`w-2 h-2 rounded-full mx-1 transition-all duration-200 ${activeKanbanCol === idx ? 'bg-blue-600 scale-125' : 'bg-gray-300'}`}
                        onClick={() => scrollToKanbanCol(idx)}
                        aria-label={`Go to column ${outreachColumns[idx].title}`}
                      />
                    ))}
                  </div>
                </div>
                {selectedOutreach && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => {
                    setSelectedOutreach(null);
                    setShowCustomStatus(false);
                    setCustomStatus('');
                  }}>
                    <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-xl shadow-2xl relative max-h-[90vh] overflow-y-auto flex flex-col justify-between" onClick={e => e.stopPropagation()}>
                      <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => {
                        setSelectedOutreach(null);
                        setShowCustomStatus(false);
                        setCustomStatus('');
                      }}><X size={24} /></button>
                      <div className="flex items-center mb-4">
                        <h2 className="text-xl font-bold flex-1">Outreach Details</h2>
                        {selectedOutreach.isDemo && (
                          <span className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Contact Name</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Contact Name" value={selectedOutreach.contact} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, contact: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Firm</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Firm" value={selectedOutreach.firm} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, firm: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Outreach Date</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Outreach Date" value={selectedOutreach.outreachDate} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, outreachDate: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Method</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Method" value={selectedOutreach.method} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, method: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Status</label>
                          {!showCustomStatus ? (
                            <select 
                              className="border rounded-lg px-3 py-2" 
                              value={selectedOutreach.status} 
                              disabled={selectedOutreach.isDemo} 
                              onChange={e => {
                                if (e.target.value === 'custom') {
                                  setShowCustomStatus(true);
                                  setCustomStatus(selectedOutreach.status);
                                } else {
                                  setSelectedOutreach({ ...selectedOutreach, status: e.target.value });
                                }
                              }}
                            >
                              <option value="">Select Status</option>
                              <option value="New">New</option>
                              <option value="To Do">To Do</option>
                              <option value="Sent">Sent</option>
                              <option value="Opened">Opened</option>
                              <option value="Contacted">Contacted</option>
                              <option value="Replied">Replied</option>
                              <option value="Meeting Booked">Meeting Booked</option>
                              <option value="Completed">Completed</option>
                              <option value="custom">+ Custom Status</option>
                            </select>
                          ) : (
                            <div className="flex gap-2">
                              <input 
                                className="flex-1 border rounded-lg px-3 py-2" 
                                placeholder="Enter custom status" 
                                value={customStatus} 
                                disabled={selectedOutreach.isDemo} 
                                onChange={e => {
                                  setCustomStatus(e.target.value);
                                  setSelectedOutreach({ ...selectedOutreach, status: e.target.value });
                                }}
                              />
                              <button 
                                onClick={() => {
                                  setShowCustomStatus(false);
                                  setCustomStatus('');
                                }}
                                className="px-3 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 transition-all"
                                disabled={selectedOutreach.isDemo}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Response</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Response" value={selectedOutreach.response} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, response: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Follow-Up Date</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Follow-Up Date" value={selectedOutreach.followUpDate} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, followUpDate: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Materials Sent</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Materials Sent" value={selectedOutreach.materialsSent} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, materialsSent: e.target.value })} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-medium text-gray-700">Notes</label>
                          <input className="border rounded-lg px-3 py-2" placeholder="Notes" value={selectedOutreach.notes} disabled={selectedOutreach.isDemo} onChange={e => setSelectedOutreach({ ...selectedOutreach, notes: e.target.value })} />
                        </div>
                      </div>
                      {!selectedOutreach.isDemo && (
                        <div className="flex gap-2 mt-6">
                          <button onClick={async () => { 
                            await updateOutreach(selectedOutreach); 
                            setSelectedOutreach(null);
                            setShowCustomStatus(false);
                            setCustomStatus('');
                          }} className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all">Save Changes</button>
                          <button onClick={() => setShowDeleteModal({ type: 'outreach', id: selectedOutreach.id })} className="flex-1 bg-red-500 text-white font-bold py-2 rounded-xl shadow-lg hover:bg-red-600 transition-all">Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Delete Confirmation Modal - Moved outside tabs */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setShowDeleteModal(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-[95vw] sm:max-w-xs shadow-2xl relative" onClick={e => e.stopPropagation()}>
              <h2 className="text-lg font-bold mb-4">Are you sure?</h2>
              <p className="mb-6">This will permanently delete this record.</p>
              <div className="flex gap-2">
                <button onClick={() => setShowDeleteModal(null)} className="flex-1 bg-gray-200 text-gray-700 font-bold py-2 rounded-xl">Cancel</button>
                <button onClick={async () => {
                  console.log('🔍 Delete button clicked');
                  console.log('📋 Delete modal state:', showDeleteModal);
                  
                  try {
                    if (showDeleteModal.type === 'outreach') {
                      console.log('🗑️ Deleting outreach:', showDeleteModal.id);
                      await deleteOutreach(showDeleteModal.id);
                    }
                    if (showDeleteModal.type === 'meeting') {
                      console.log('🗑️ Deleting meeting:', showDeleteModal.id);
                      await deleteMeeting(showDeleteModal.id);
                    }
                    if (showDeleteModal.type === 'investor') {
                      console.log('🗑️ Deleting investor:', showDeleteModal.id);
                      await deleteInvestor(showDeleteModal.id);
                    }
                    
                    console.log('✅ Delete operation completed successfully');
                    
                    // Close all modals
                    console.log('🔒 Closing all modals');
                    setShowDeleteModal(null);
                    setShowModal(false);
                    setSelectedInvestor(null);
                    setSelectedOutreach(null);
                    setShowCalendarModal(false);
                    setShowDateModal(false);
                    setShowSuccessModal(false);
                    
                    // Navigate to investor-tracker
                    console.log('🧭 Navigating to /investor-tracker');
                    navigate('/investor-tracker');
                    
                  } catch (error) {
                    console.error('❌ Error during delete operation:', error);
                    // Still close the delete modal on error
                    setShowDeleteModal(null);
                  }
                }} className="flex-1 bg-red-500 text-white font-bold py-2 rounded-xl hover:bg-red-600">Delete</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pipeline' && (
          <>
            {/* Calendar Modal */}
            {showCalendarModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => setShowCalendarModal(false)}>
                <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-4xl shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setShowCalendarModal(false)}><X size={24} /></button>
                  <h2 className="text-xl font-bold mb-4">Calendar</h2>
                  <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-4 md:p-8 border border-white/20">
                    <FullCalendar
                      plugins={[dayGridPlugin, interactionPlugin]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: ''
                      }}
                      height="auto"
                      aspectRatio={1.7}
                      events={[
                        ...(selectedCompany?.isDemo ? meetingPipeline : [demoMeeting, ...meetingPipeline]).map(m => ({
                          id: m.id,
                          title: m.contact + (m.isDemo ? ' (Demo)' : ''),
                          start: m.meetingDate,
                          extendedProps: m,
                          backgroundColor: m.isDemo ? '#a5b4fc' : '#6ee7b7',
                          borderColor: m.isDemo ? '#6366f1' : '#10b981',
                          textColor: '#22223b',
                          classNames: m.isDemo ? 'font-bold' : ''
                        }))
                      ]}
                      eventClick={info => {
                        info.jsEvent.preventDefault();
                        setSelectedInvestor(info.event.extendedProps);
                      }}
                      dateClick={({ date }) => {
                        const dateStr = date.toISOString().slice(0, 10);
                        setSelectedDate(dateStr);
                        setSelectedDateMeetings((selectedCompany?.isDemo ? meetingPipeline : [demoMeeting, ...meetingPipeline]).filter(m => m.meetingDate === dateStr));
                        setShowDateModal(true);
                      }}
                      dayMaxEventRows={2}
                      eventContent={renderEventContent}
                      dayCellClassNames={({ date }) => {
                        const today = new Date().toISOString().slice(0, 10);
                        const dateStr = date.toISOString().slice(0, 10);
                        return dateStr === today ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold rounded-lg shadow-lg' : '';
                      }}
                      contentHeight="auto"
                      fixedWeekCount={false}
                      displayEventTime={false}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        {showDateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => setShowDateModal(false)}>
            <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-lg shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setShowDateModal(false)}><X size={24} /></button>
              <h2 className="text-xl font-bold mb-4">Meetings for {selectedDate}</h2>
              <div className="flex flex-col gap-2 mb-4">
                {selectedDateMeetings.length === 0 && <div className="text-gray-500">No meetings for this date.</div>}
                {selectedDateMeetings.map(meeting => (
                  <div key={meeting.id} className="bg-gradient-to-br from-green-100 to-purple-100 rounded-lg px-3 py-2 flex items-center gap-2 cursor-pointer hover:shadow" onClick={() => { setSelectedInvestor(meeting); setShowDateModal(false); }}>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="font-medium truncate">{meeting.contact}</span>
                    {meeting.isDemo && <span className="ml-1 px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">Demo</span>}
                  </div>
                ))}
              </div>
              <button className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all" onClick={() => {
                setShowDateModal(false);
                setShowModal(true);
                setModalType('addMeeting');
                setNewMeeting(m => ({ ...m, meetingDate: selectedDate }));
              }}>+ Add Meeting</button>
            </div>
          </div>
        )}
        
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={() => setShowSuccessModal(false)}>
            <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-sm shadow-2xl relative" onClick={e => e.stopPropagation()}>
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <CheckCircle size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Success!</h3>
                <p className="text-sm text-gray-600 mb-6">{successMessage}</p>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-2 rounded-xl shadow-lg hover:from-blue-600 hover:to-purple-700 transition-all"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    {/* </div> */}
  </>
);
}

export default InvestorTracker; 